import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { EventNames, trackEvent } from "../analytics";
import { canonicalUrl } from "../siteConfig";

const ASSESSMENT_REQUEST_EMAIL = process.env.REACT_APP_AUDIT_REQUEST_EMAIL || "hello@optisiteseo.com";

const primaryConcernOptions = [
  "Technical SEO",
  "Discoverability",
  "Indexing",
  "Performance",
  "AI Visibility",
  "Analytics & Measurement",
  "Other",
];

function getInitialWebsite(search) {
  const params = new URLSearchParams(search);
  return params.get("website") || "";
}

export default function RequestAssessmentPage() {
  const location = useLocation();
  const startedTrackedRef = useRef(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    website: getInitialWebsite(location.search),
    email: "",
    phone: "",
    primaryConcern: "Technical SEO",
    additionalNotes: "",
  });
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [lastMailto, setLastMailto] = useState("");
  const trackScanCta = (ctaId) => trackEvent(EventNames.CtaClicked, { cta_id: ctaId });

  const trackStarted = useCallback((source) => {
    if (startedTrackedRef.current) return;
    startedTrackedRef.current = true;
    trackEvent(EventNames.AssessmentRequestStarted, { source });
  }, []);

  useEffect(() => {
    trackStarted("page_load");
  }, [trackStarted]);

  const updateField = (field, value) => {
    trackStarted("form_interaction");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const cleaned = {
      name: form.name.trim(),
      company: form.company.trim(),
      website: form.website.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      primaryConcern: form.primaryConcern,
      additionalNotes: form.additionalNotes.trim(),
    };

    if (!cleaned.name || !cleaned.company || !cleaned.website || !cleaned.email || !cleaned.primaryConcern) {
      setError("Complete the required fields before sending the assessment request.");
      setConfirmation("");
      return;
    }

    const subject = `Technical SEO & Visibility Assessment request for ${cleaned.company}`;
    const body = [
      "Hi OptiSiteSEO,",
      "",
      "I would like to request a Technical SEO & Visibility Assessment.",
      "",
      `Name: ${cleaned.name}`,
      `Company: ${cleaned.company}`,
      `Website: ${cleaned.website}`,
      `Email: ${cleaned.email}`,
      `Phone: ${cleaned.phone || "Not provided"}`,
      `Primary concern: ${cleaned.primaryConcern}`,
      "",
      "Additional notes:",
      cleaned.additionalNotes || "None provided",
      "",
      "Please send next steps for intake, scope, and payment.",
    ].join("\n");
    const mailto = `mailto:${ASSESSMENT_REQUEST_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    setError("");
    setLastMailto(mailto);
    setConfirmation(
      `Your email app should open with a prefilled request. Send that email to ${ASSESSMENT_REQUEST_EMAIL}. If it does not open, use the fallback link below.`
    );
    trackEvent(EventNames.AssessmentRequestSubmitted, {
      primary_concern: cleaned.primaryConcern,
      has_phone: Boolean(cleaned.phone),
      has_notes: Boolean(cleaned.additionalNotes),
    });
    window.location.href = mailto;
  };

  return (
    <div className="app page-grid">
      <Helmet>
        <title>Request Assessment | OptiSiteSEO</title>
        <meta
          name="description"
          content="Request a Technical SEO & Visibility Assessment for technical SEO issues, crawlability, indexing, performance, AI discoverability, and measurement gaps."
        />
        <link rel="canonical" href={canonicalUrl("/request-assessment")} />
        <meta property="og:title" content="Request a Technical SEO & Visibility Assessment | OptiSiteSEO" />
        <meta
          property="og:description"
          content="Send intake details for a paid diagnostic assessment with a prioritized implementation roadmap."
        />
        <meta property="og:url" content={canonicalUrl("/request-assessment")} />
      </Helmet>

      <nav className="top-nav">
        <div className="brand">
          <span>OptiSiteSEO</span>
        </div>
        <div className="nav-links">
          <a className="link" href="/">
            Home
          </a>
          <a className="link" href="/technical-visibility-audit">
            Assessment Details
          </a>
          <a className="primary" href="/app/index.html" onClick={() => trackScanCta("request-assessment-nav-scan")}>
            Run Free SEO Scan
          </a>
        </div>
      </nav>

      <section className="card section page-hero">
        <p className="eyebrow">Request assessment</p>
        <h1>Request a Technical SEO & Visibility Assessment</h1>
        <p className="subtext">
          Send the context needed to scope a paid diagnostic review of technical SEO issues, discoverability blockers, crawlability and indexing
          problems, performance and measurement gaps, AI discoverability readiness, and implementation priorities.
        </p>
        <div className="badge-row">
          <span className="badge">Starter $297</span>
          <span className="badge">Growth $697</span>
          <span className="badge">Advanced $1,500</span>
        </div>
      </section>

      <form className="card section audit-intake-form" onSubmit={handleSubmit}>
        <label>
          Name
          <input type="text" value={form.name} onChange={(event) => updateField("name", event.target.value)} required />
        </label>
        <label>
          Company
          <input type="text" value={form.company} onChange={(event) => updateField("company", event.target.value)} required />
        </label>
        <label>
          Website
          <input
            type="url"
            value={form.website}
            onChange={(event) => updateField("website", event.target.value)}
            placeholder="https://www.yoursite.com"
            required
          />
        </label>
        <label>
          Email
          <input type="email" value={form.email} onChange={(event) => updateField("email", event.target.value)} required />
        </label>
        <label>
          Phone
          <input type="tel" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} />
        </label>
        <label>
          Primary Concern
          <select value={form.primaryConcern} onChange={(event) => updateField("primaryConcern", event.target.value)} required>
            {primaryConcernOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label className="full-span">
          Additional Notes
          <textarea
            value={form.additionalNotes}
            onChange={(event) => updateField("additionalNotes", event.target.value)}
            placeholder="What technical SEO, visibility, indexing, performance, AI visibility, or measurement issue should be reviewed first?"
            rows="5"
          />
        </label>
        {error && <div className="error full-span">{error}</div>}
        {confirmation && (
          <div className="status full-span">
            {confirmation}
            {lastMailto && (
              <div style={{ marginTop: 10 }}>
                <a className="secondary" href={lastMailto}>
                  Open prefilled email again
                </a>
              </div>
            )}
          </div>
        )}
        <div className="form-actions full-span">
          <button type="submit" className="primary">
            Send Request
          </button>
          <a className="secondary" href="/technical-visibility-audit">
            View Assessment Details
          </a>
        </div>
      </form>
    </div>
  );
}
